import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    AGGREGATE_NAMES,
    EUROPACE_ERROR_RESPONSE_MESSAGES,
    EUROPACE_PROCESS_EVENTS_REQUEST_TYPES,
    LEAD_ERROR_RESPONSE_MESSAGES,
    LOAN_REQUEST_COMMANDS,
} from 'cpool-constants';
import type { EventSchema, LoanRequest_EuropaceProcessEventsRequested } from 'cpool-events';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { verifyEuropaceProcessEventsRequestStatus } from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/utils/verifyEuropaceProcessEventsRequestStatus.tsx';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

export const useEuropaceProcessEventsMutation = ({
    leadId,
    processId,
}: {
    leadId: string;
    processId: string | null | undefined;
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!processId) {
                throw new Error(EUROPACE_ERROR_RESPONSE_MESSAGES.PROCESS_NOT_IMPORTED);
            }
            const response = await APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.requestEuropaceProcessEvents,
                payload: {
                    processId,
                    trigger: EUROPACE_PROCESS_EVENTS_REQUEST_TYPES.MANUAL,
                } satisfies EventSchema<LoanRequest_EuropaceProcessEventsRequested>,
            });
            return response.position;
        },
        onError: (error) => {
            const errorMessage = 'response' in error ? error.response : error.message;
            switch (errorMessage) {
                case LEAD_ERROR_RESPONSE_MESSAGES.LEAD_NOT_FOUND:
                    toast.info(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.ERROR_RESPONSE.NOT_FOUND'));
                    break;
                case EUROPACE_ERROR_RESPONSE_MESSAGES.PROCESS_NOT_IMPORTED:
                    toast.error(
                        t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.ERROR_RESPONSE.PROCESS_NOT_IMPORTED'),
                    );
                    break;
                default:
                    toast.error(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.ERROR_RESPONSE.REQUEST_ERROR'));
                    break;
            }
        },
        onSuccess: async (position) => {
            await verifyEuropaceProcessEventsRequestStatus({
                loanRequestId: leadId,
                t,
            });
            const queryKey = queryKeys.getEuropaceProcessEvents(leadId);
            setQueryPosition(JSON.stringify(queryKey), position);
            queryClient.invalidateQueries({ queryKey });
        },
    });
};
